import {
  CONTRACT_RENEWAL_STATUS_COLORS,
  CONTRACT_RENEWAL_STATUS_DISPLAY_NAMES,
  CONTRACT_SOW_STATUS_COLORS,
  CONTRACT_SOW_STATUS_DISPLAY_NAMES,
  ContractRenewalStatusEnum,
  ContractSowStatusEnum,
  DEFAULT_CONTRACT_LEGAL_STATUSES,
} from '../contract'
import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition } from '../module'

export const CONTRACT_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  // System columns (always visible)
  {
    id: 'title',
    name: 'title',
    label: 'Contract title',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: true,
    isFixed: true,
    isVisible: true,
    isRequired: true,
    order: 1,
    width: '300px',
    minWidth: '200px',
  },

  // Standard columns
  {
    id: 'entityId',
    name: 'entityId',
    label: 'Client',
    description: 'Client associated with this contract',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 2,
    width: '220px',
    minWidth: '160px',
  },
  {
    id: 'projectId',
    name: 'projectId',
    label: 'Project',
    description: 'Project associated with this contract',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 3,
    width: '220px',
    minWidth: '160px',
  },
  {
    id: 'contractNumber',
    name: 'contractNumber',
    label: 'Contract number',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 4,
    width: '180px',
    minWidth: '100px',
  },
  {
    id: 'description',
    name: 'description',
    label: 'Description',
    fieldType: FieldTypeEnum.enum.TEXT,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 5,
    width: '350px',
    minWidth: '200px',
  },
  {
    id: 'status',
    name: 'status',
    label: 'Legal status',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 6,
    width: '130px',
    minWidth: '100px',
    options: DEFAULT_CONTRACT_LEGAL_STATUSES.map(status => ({
      value: status.id,
      label: status.name,
      color: status.color,
    })),
  },
  {
    id: 'tags',
    name: 'tags',
    label: 'Tags',
    description: 'Tags for organizing and categorizing the contract',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: true,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 90, // Positioned near end of standard columns, custom fields come after at 91+
    width: '200px',
    minWidth: '150px',
  },
  {
    id: 'sowStatus',
    name: 'sowStatus',
    label: 'SOW status',
    description: 'Operational statement of work workflow status',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 7,
    width: '150px',
    minWidth: '120px',
    options: Object.values(ContractSowStatusEnum).map(status => ({
      value: status,
      label: CONTRACT_SOW_STATUS_DISPLAY_NAMES[status],
      color: CONTRACT_SOW_STATUS_COLORS[status],
    })),
  },
  {
    id: 'renewalStatus',
    name: 'renewalStatus',
    label: 'Renewal status',
    description: 'Operational renewal workflow status',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 8,
    width: '150px',
    minWidth: '120px',
    options: Object.values(ContractRenewalStatusEnum).map(status => ({
      value: status,
      label: CONTRACT_RENEWAL_STATUS_DISPLAY_NAMES[status],
      color: CONTRACT_RENEWAL_STATUS_COLORS[status],
    })),
  },
  {
    id: 'renewalOwnerId',
    name: 'renewalOwnerId',
    label: 'Renewal owner',
    description: 'Internal owner accountable for renewal follow-up',
    fieldType: FieldTypeEnum.enum.USER,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 9,
    width: '180px',
    minWidth: '140px',
  },
  {
    id: 'renewalDueDate',
    name: 'renewalDueDate',
    label: 'Renewal due',
    description: 'Target date for renewal action',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 10,
    width: '150px',
    minWidth: '120px',
  },
  {
    id: 'totalValue',
    name: 'totalValue',
    label: 'Total value',
    fieldType: FieldTypeEnum.enum.NUMBER,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 11,
    width: '150px',
    minWidth: '100px',
  },
  {
    id: 'startDate',
    name: 'startDate',
    label: 'Start date',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 12,
    width: '150px',
    minWidth: '120px',
  },
  {
    id: 'endDate',
    name: 'endDate',
    label: 'End date',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 13,
    width: '150px',
    minWidth: '120px',
  },
  {
    id: 'contractType',
    name: 'contractType',
    label: 'Contract type',
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    isSystem: false,
    isFixed: false,
    isVisible: true,
    isRequired: false,
    order: 14,
    width: '200px',
    minWidth: '100px',
  },
  {
    id: 'paymentTerms',
    name: 'paymentTerms',
    label: 'Payment terms',
    fieldType: FieldTypeEnum.enum.TEXT,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 15,
    width: '200px',
    minWidth: '150px',
  },
  {
    id: 'isRenewable',
    name: 'isRenewable',
    label: 'Renewable',
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 16,
    width: '100px',
    minWidth: '80px',
  },

  // Signatories: who signed and when, per party. Hidden by default so the
  // table layout is unchanged; surfaced for editing through the overview
  // InfoPanel, which is the only way to amend these after creation.
  {
    id: 'signedByEntityName',
    name: 'signedByEntityName',
    label: 'Client signatory',
    description: 'Name of the person who signed for the client',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 17,
    width: '180px',
    minWidth: '140px',
  },
  {
    id: 'signedByClient',
    name: 'signedByClient',
    label: 'Client signed',
    description: 'Date the client signed',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 18,
    width: '150px',
    minWidth: '120px',
  },
  {
    id: 'signedByOrgName',
    name: 'signedByOrgName',
    label: 'Org signatory',
    description: 'Name of the person who signed for your organization',
    fieldType: FieldTypeEnum.enum.STRING,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 19,
    width: '180px',
    minWidth: '140px',
  },
  {
    id: 'signedByOrg',
    name: 'signedByOrg',
    label: 'Org signed',
    description: 'Date your organization countersigned',
    fieldType: FieldTypeEnum.enum.DATE,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 20,
    width: '150px',
    minWidth: '120px',
  },
  // Metadata columns
  {
    id: 'createdAt',
    name: 'createdAt',
    label: 'Created',
    fieldType: FieldTypeEnum.enum.DATETIME,
    dateOnly: true,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 21,
    width: '150px',
    minWidth: '120px',
  },
  {
    id: 'updatedAt',
    name: 'updatedAt',
    label: 'Updated',
    fieldType: FieldTypeEnum.enum.DATETIME,
    dateOnly: true,
    isSystem: false,
    isFixed: false,
    isVisible: false,
    isRequired: false,
    order: 22,
    width: '150px',
    minWidth: '120px',
  },
]
